import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import EnquiryForm from "../components/EnquiryForm";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const PHONES = [
  { num: "+40 723 250 260", tel: "+40723250260", wa: "40723250260", id: "phone-1" },
  { num: "+40 769 229 900", tel: "+40769229900", wa: "40769229900", id: "phone-2" },
];

const EMAILS = ["management.kataco@gmail.com", "kat_acoworldwide@yahoo.com"];

export default function Contact() {
  const { t } = useLang();
  const faq = t("contact.faq");

  return (
    <div data-testid="contact-page" className="pt-32 pb-24">
      <Seo title={t("seo.contactTitle")} description={t("seo.contactDesc")} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50">{t("contact.title")}</h1>
          <p className="mt-5 text-slate-400 max-w-xl">{t("contact.sub")}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Reveal>
            <div data-testid="contact-card-phone" className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-6">
              <Phone size={22} className="text-[#4499F7]" />
              <h3 className="mt-5 font-display font-semibold text-slate-50">{t("contact.phone")}</h3>
              <div className="mt-3 space-y-2.5">
                {PHONES.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2">
                    <a data-testid={`contact-${p.id}`} href={`tel:${p.tel}`} className="text-sm text-slate-300 hover:text-[#4499F7] transition-colors font-mono-num">
                      {p.num}
                    </a>
                    <a
                      data-testid={`contact-${p.id}-whatsapp`}
                      href={`https://wa.me/${p.wa}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="WhatsApp"
                      className="text-slate-500 hover:text-[#25D366] transition-colors"
                    >
                      <MessageCircle size={16} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div data-testid="contact-card-email" className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-6">
              <Mail size={22} className="text-[#4499F7]" />
              <h3 className="mt-5 font-display font-semibold text-slate-50">{t("contact.email")}</h3>
              <div className="mt-3 space-y-2.5">
                {EMAILS.map((e) => (
                  <a key={e} data-testid={`contact-email-${e}`} href={`mailto:${e}`} className="block text-sm text-slate-300 hover:text-[#4499F7] transition-colors break-all">
                    {e}
                  </a>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div data-testid="contact-card-address" className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-6">
              <MapPin size={22} className="text-[#4499F7]" />
              <h3 className="mt-5 font-display font-semibold text-slate-50">{t("contact.addr")}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">Șoseaua Unirii 185, Balotesti, Ilfov, România</p>
            </div>
          </Reveal>

          <Reveal delay={0.24}>
            <div data-testid="contact-card-hours" className="h-full bg-[#12141D] border border-white/10 rounded-2xl p-6">
              <Clock size={22} className="text-[#4499F7]" />
              <h3 className="mt-5 font-display font-semibold text-slate-50">{t("contact.hours")}</h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">{t("contact.hoursVal")}</p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-8 items-stretch">
          <Reveal>
            <div className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 lg:p-10 spotlight-card overflow-hidden">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-50 mb-8">{t("contact.formTitle")}</h2>
              <EnquiryForm testPrefix="contact" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl overflow-hidden border border-white/10 min-h-[420px]" data-testid="contact-map">
              <iframe
                title="KATACO location"
                src="https://www.google.com/maps?q=%C8%98oseaua%20Unirii%20185%2C%20Balotesti%2C%20Ilfov%2C%20Romania&output=embed"
                className="w-full h-full min-h-[420px] grayscale invert-[0.92] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: (Array.isArray(faq) ? faq : []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            })}
          </script>
        </Helmet>
        <div className="mt-20 max-w-3xl" data-testid="contact-faq">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{t("contact.faqTitle")}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-8">
              {Array.isArray(faq) && faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display font-semibold text-slate-100 hover:text-[#4499F7] hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
